import { zodResolver } from '@hookform/resolvers/zod';
import { Check, LoaderCircle, Paperclip } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { SelectedFilesList } from './SelectedFilesList';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { TicketPriority } from '@/services/TicketsService';

const PRIORITIES = ['low', 'high', 'critical'] as const;

function areSameFile(fileA: File, fileB: File) {
  return (
    fileA.name === fileB.name &&
    fileA.size === fileB.size &&
    fileA.lastModified === fileB.lastModified
  );
}

interface CreateTicketFormCardText {
  openNewTicket: string;
  subjectLabel: string;
  subjectPlaceholder: string;
  priorityLabel: string;
  messageLabel: string;
  messagePlaceholder: string;
  imagesLabel: string;
  imagesHint: string;
  createAttachFilesButton: string;
  createTicketButton: string;
  priorityLabels: Record<TicketPriority, string>;
  ticketSubjectRequired: string;
  addMessageOrImage: string;
  invalidFileType: (name: string) => string;
  maxFileSize: (name: string) => string;
  maxImagesPerMessage: string;
}

interface CreateTicketFormValues {
  subject: string;
  priority: TicketPriority;
  message: string;
  files: File[];
}

interface CreateTicketFormCardProps {
  text: CreateTicketFormCardText;
  isSubmitting: boolean;
  maxAttachmentsPerMessage: number;
  maxImageSizeBytes: number;
  allowedMimeTypes: readonly string[];
  onSubmit: (values: CreateTicketFormValues) => Promise<boolean>;
}

export function CreateTicketFormCard({
  text,
  isSubmitting,
  maxAttachmentsPerMessage,
  maxImageSizeBytes,
  allowedMimeTypes,
  onSubmit,
}: CreateTicketFormCardProps) {
  const schema = useMemo(
    () =>
      z
        .object({
          subject: z.string().trim().min(1, text.ticketSubjectRequired),
          priority: z.enum(PRIORITIES),
          message: z.string(),
          files: z
            .array(z.custom<File>())
            .max(maxAttachmentsPerMessage, text.maxImagesPerMessage),
        })
        .superRefine((data, context) => {
          if (data.message.trim().length === 0 && data.files.length === 0) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: text.addMessageOrImage,
              path: ['message'],
            });
          }
        }),
    [
      maxAttachmentsPerMessage,
      text.addMessageOrImage,
      text.maxImagesPerMessage,
      text.ticketSubjectRequired,
    ],
  );

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<CreateTicketFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      subject: '',
      priority: 'low',
      message: '',
      files: [],
    },
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const files = watch('files');

  function handleFilesSelection(incomingFiles: File[]) {
    const updatedFiles = [...files];

    for (const file of incomingFiles) {
      if (!allowedMimeTypes.includes(file.type)) {
        toast.error(text.invalidFileType(file.name));
        continue;
      }

      if (file.size > maxImageSizeBytes) {
        toast.error(text.maxFileSize(file.name));
        continue;
      }

      const alreadyAdded = updatedFiles.some((addedFile) =>
        areSameFile(addedFile, file),
      );

      if (alreadyAdded) {
        continue;
      }

      if (updatedFiles.length >= maxAttachmentsPerMessage) {
        toast.error(text.maxImagesPerMessage);
        break;
      }

      updatedFiles.push(file);
    }

    setValue('files', updatedFiles, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  const onValidSubmit = handleSubmit(async (values) => {
    const succeeded = await onSubmit(values);

    if (succeeded) {
      reset({
        subject: '',
        priority: 'low',
        message: '',
        files: [],
      });
    }
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{text.openNewTicket}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={onValidSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {text.subjectLabel}
            </label>
            <Input
              placeholder={text.subjectPlaceholder}
              disabled={isSubmitting}
              {...register('subject')}
            />
            {errors.subject?.message && (
              <p className="text-xs text-red-600">{errors.subject.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {text.priorityLabel}
            </label>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value: TicketPriority) =>
                    field.onChange(value)
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      {text.priorityLabels.low}
                    </SelectItem>
                    <SelectItem value="high">
                      {text.priorityLabels.high}
                    </SelectItem>
                    <SelectItem value="critical">
                      {text.priorityLabels.critical}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              {text.messageLabel}
            </label>
            <Textarea
              placeholder={text.messagePlaceholder}
              className="min-h-[100px]"
              disabled={isSubmitting}
              {...register('message')}
            />
            {errors.message?.message && (
              <p className="text-xs text-red-600">{errors.message.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {text.imagesLabel}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept={allowedMimeTypes.join(', ')}
              multiple
              onChange={(event) => {
                handleFilesSelection(Array.from(event.target.files ?? []));
                event.target.value = '';
              }}
              disabled={isSubmitting}
              className="hidden"
            />

            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={isSubmitting}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4" />
              {text.createAttachFilesButton}
            </Button>

            <p className="text-xs text-gray-500">{text.imagesHint}</p>

            {errors.files?.message && (
              <p className="text-xs text-red-600">{errors.files.message}</p>
            )}

            <SelectedFilesList
              files={files}
              disabled={isSubmitting}
              onRemove={(index) => {
                setValue(
                  'files',
                  files.filter((_, currentIndex) => currentIndex !== index),
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  },
                );
              }}
            />
          </div>

          <Button type="submit" className="gap-2" disabled={isSubmitting}>
            {isSubmitting ? (
              <LoaderCircle className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {text.createTicketButton}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
